import { useMarkets } from '@/state/markets';
import { useSurfaces } from '@/state/surfaces';
import { useSettings } from '@/state/settings';
import { usePhone } from '@/state/phone';
import { useXClone } from '@/state/xclone';
import { usePower } from '@/state/power';
import { useEmail } from '@/state/email';
import { useLife } from '@/state/life';
import { useWallet } from '@/state/wallet';
import { useMeta } from '@/state/meta';
import { useCalendar } from '@/state/calendar';
import { useDialogue } from '@/state/dialogue';
import { clearAllArcsForAscension } from './arcs';
import { clearEnginePersist } from './tick';
import { coinsForRank } from '@/lib/tier';
import { useCinematic } from '@/components/CinematicTakeover';

const SAVE_KEY = 'nasdaq.one:save:v1';
const SCHEMA_VERSION = 3;

interface SaveBlob {
  schemaVersion: number;
  savedAt: number;
  markets: {
    cash: number;
    positions: ReturnType<typeof useMarkets.getState>['positions'];
    openOrders: ReturnType<typeof useMarkets.getState>['openOrders'];
    realizedPnl: number;
    recentFills: ReturnType<typeof useMarkets.getState>['recentFills'];
    activeTickerId: string;
    equityATH?: number;
  };
  surfaces: {
    active: ReturnType<typeof useSurfaces.getState>['active'];
    unreadCounts: ReturnType<typeof useSurfaces.getState>['unreadCounts'];
  };
  settings: {
    muted: boolean;
    reducedMotion: boolean;
    showLifeDashboard: boolean;
    showRepDashboard: boolean;
    themeId: string;
    playerHandle?: string;
  };
  phone: {
    contacts: ReturnType<typeof usePhone.getState>['contacts'];
    messages: ReturnType<typeof usePhone.getState>['messages'];
    activeThreadId: string | null;
  };
  xclone: {
    posts: ReturnType<typeof useXClone.getState>['posts'];
    followers: number;
  };
  power: {
    tribeRep: ReturnType<typeof usePower.getState>['tribeRep'];
    notoriety: number;
    influence: number;
  };
  email: {
    emails: ReturnType<typeof useEmail.getState>['emails'];
    selectedId: string | null;
  };
  life: {
    relationships: ReturnType<typeof useLife.getState>['relationships'];
    health: number;
    sanity: number;
    irsHeat: number;
    secHeat: number;
    fbiHeat: number;
    apartmentTier: ReturnType<typeof useLife.getState>['apartmentTier'];
    insuranceStatus: ReturnType<typeof useLife.getState>['insuranceStatus'];
    creditScore: number;
    lawyerRetainerActiveSince: number | null;
    lastDecayMs: number;
  };
  wallet: {
    possessions: ReturnType<typeof useWallet.getState>['possessions'];
    ownsCharityFoundation: boolean;
    ownsYacht: boolean;
    ownsJet: boolean;
    ownsIsland?: boolean;
    ownsSportsTeam?: boolean;
    ownsNewspaper?: boolean;
  };
  meta: {
    ascensions: number;
    carryCoins: number;
    highestTierRankReached: number;
    graveyard: ReturnType<typeof useMeta.getState>['graveyard'];
    loreCodex: ReturnType<typeof useMeta.getState>['loreCodex'];
    unlockedAchievements: string[];
    lastLoginDate: string;
    loginStreak: number;
    buffs: ReturnType<typeof useMeta.getState>['buffs'];
  };
  calendar: { events: ReturnType<typeof useCalendar.getState>['events'] };
  dialogue?: {
    firedPairs: Record<string, string[]>;
    usedOneShots: Record<string, string[]>;
  };
}

function snapshot(): SaveBlob {
  const m = useMarkets.getState();
  const s = useSurfaces.getState();
  const set = useSettings.getState();
  const p = usePhone.getState();
  const x = useXClone.getState();
  const pw = usePower.getState();
  const em = useEmail.getState();
  const lf = useLife.getState();
  const w = useWallet.getState();
  const meta = useMeta.getState();
  const cal = useCalendar.getState();
  return {
    schemaVersion: SCHEMA_VERSION,
    savedAt: Date.now(),
    markets: {
      cash: m.cash,
      positions: m.positions,
      openOrders: m.openOrders,
      realizedPnl: m.realizedPnl,
      recentFills: m.recentFills,
      activeTickerId: m.activeTickerId,
      equityATH: m.equityATH,
    },
    surfaces: {
      active: s.active,
      unreadCounts: s.unreadCounts,
    },
    settings: {
      muted: set.muted,
      reducedMotion: set.reducedMotion,
      showLifeDashboard: set.showLifeDashboard,
      showRepDashboard: set.showRepDashboard,
      themeId: set.themeId,
      playerHandle: set.playerHandle,
    },
    phone: {
      contacts: p.contacts,
      messages: p.messages,
      activeThreadId: p.activeThreadId,
    },
    xclone: {
      posts: x.posts,
      followers: x.followers,
    },
    power: {
      tribeRep: pw.tribeRep,
      notoriety: pw.notoriety,
      influence: pw.influence,
    },
    email: {
      emails: em.emails,
      selectedId: em.selectedId,
    },
    life: {
      relationships: lf.relationships,
      health: lf.health,
      sanity: lf.sanity,
      irsHeat: lf.irsHeat,
      secHeat: lf.secHeat,
      fbiHeat: lf.fbiHeat,
      apartmentTier: lf.apartmentTier,
      insuranceStatus: lf.insuranceStatus,
      creditScore: lf.creditScore,
      lawyerRetainerActiveSince: lf.lawyerRetainerActiveSince,
      lastDecayMs: lf.lastDecayMs,
    },
    wallet: {
      possessions: w.possessions,
      ownsCharityFoundation: w.ownsCharityFoundation,
      ownsYacht: w.ownsYacht,
      ownsJet: w.ownsJet,
      ownsIsland: w.ownsIsland,
      ownsSportsTeam: w.ownsSportsTeam,
      ownsNewspaper: w.ownsNewspaper,
    },
    meta: {
      ascensions: meta.ascensions,
      carryCoins: meta.carryCoins,
      highestTierRankReached: meta.highestTierRankReached,
      graveyard: meta.graveyard,
      loreCodex: meta.loreCodex,
      unlockedAchievements: meta.unlockedAchievements,
      lastLoginDate: meta.lastLoginDate,
      loginStreak: meta.loginStreak,
      buffs: meta.buffs,
    },
    calendar: { events: cal.events },
    dialogue: {
      firedPairs: useDialogue.getState().firedPairs,
      usedOneShots: useDialogue.getState().usedOneShots,
    },
  };
}

export function saveNow(): void {
  try {
    localStorage.setItem(SAVE_KEY, JSON.stringify(snapshot()));
  } catch (err) {
    console.error('[save] failed:', err);
  }
}

export function loadSave(): { loaded: boolean; offlineMs: number } {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return { loaded: false, offlineMs: 0 };
    const parsed = JSON.parse(raw) as Partial<SaveBlob>;
    if (parsed.schemaVersion !== SCHEMA_VERSION) {
      console.warn(`[save] unrecognized schema ${parsed.schemaVersion}, ignoring`);
      return { loaded: false, offlineMs: 0 };
    }
    if (parsed.markets) useMarkets.setState(parsed.markets);
    if (parsed.surfaces) useSurfaces.setState(parsed.surfaces);
    if (parsed.settings) useSettings.setState(parsed.settings);
    if (parsed.phone) usePhone.setState(parsed.phone);
    if (parsed.xclone) useXClone.setState(parsed.xclone);
    if (parsed.power) usePower.setState(parsed.power);
    if (parsed.email) useEmail.setState(parsed.email);
    if (parsed.life) useLife.setState(parsed.life);
    if (parsed.wallet) {
      // Migrate possessions: keep saved `available` flags but always use
      // current seed labels/descriptions so writing edits propagate.
      const seedPossessions = useWallet.getState().possessions;
      const incomingPossessions = parsed.wallet.possessions ?? [];
      const mergedPossessions = seedPossessions.map((seed) => {
        const saved = incomingPossessions.find((p) => p.id === seed.id);
        return saved ? { ...seed, available: saved.available } : seed;
      });
      useWallet.setState({ ...parsed.wallet, possessions: mergedPossessions });
    }
    if (parsed.meta) {
      // Defensive merge: ensure buffs object has all expected fields even if
      // the saved blob predates a recent buff-schema change.
      const cur = useMeta.getState();
      const incoming = parsed.meta;
      const mergedBuffs = { ...cur.buffs, ...(incoming.buffs ?? {}) };
      useMeta.setState({ ...incoming, buffs: mergedBuffs });
    }
    if (parsed.calendar) useCalendar.setState(parsed.calendar);
    if (parsed.dialogue) {
      useDialogue.setState({
        firedPairs: parsed.dialogue.firedPairs ?? {},
        usedOneShots: parsed.dialogue.usedOneShots ?? {},
        followUpsByThread: {},     // ephemeral — reset on reload
        pendingReplyByThread: {},   // ephemeral — reset on reload
      });
    }
    const offlineMs = parsed.savedAt ? Math.max(0, Date.now() - parsed.savedAt) : 0;
    return { loaded: true, offlineMs };
  } catch (err) {
    console.error('[save] load failed:', err);
    return { loaded: false, offlineMs: 0 };
  }
}

let saveTimer: number | undefined;
function scheduleSave(delayMs = 1000): void {
  if (saveTimer !== undefined) window.clearTimeout(saveTimer);
  saveTimer = window.setTimeout(saveNow, delayMs);
}

export function attachAutosave(): void {
  useMarkets.subscribe(() => scheduleSave());
  useSurfaces.subscribe(() => scheduleSave());
  useSettings.subscribe(() => scheduleSave());
  usePhone.subscribe(() => scheduleSave());
  useXClone.subscribe(() => scheduleSave());
  usePower.subscribe(() => scheduleSave());
  useEmail.subscribe(() => scheduleSave());
  useLife.subscribe(() => scheduleSave());
  useWallet.subscribe(() => scheduleSave());
  useMeta.subscribe(() => scheduleSave());
  useCalendar.subscribe(() => scheduleSave());
  useDialogue.subscribe(() => scheduleSave());

  // also save on page hide
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') saveNow();
  });
}

export function resetSave(): void {
  localStorage.removeItem(SAVE_KEY);
  clearEnginePersist();
}

/**
 * Soft restart per META.md §Ascension flow.
 * - Awards Carry Coins based on highest tier reached this run
 * - Records Graveyard entry for the retiring character
 * - Resets per-run state (markets/phone/xclone/email/life/wallet)
 * - Preserves meta state (ascensions/carryCoins/highestTier/graveyard)
 * - Saves and triggers an ascension cinematic
 */
export function ascend(): void {
  const meta = useMeta.getState();
  const m = useMarkets.getState();
  let posValue = 0;
  for (const p of m.positions) {
    posValue += p.qty * p.avgPrice;
  }
  const finalNetWorth = m.cash + posValue;
  const earned = coinsForRank(meta.highestTierRankReached);

  meta.recordAscension({
    characterId: `char-${meta.ascensions + 1}`,
    ascendedAt: Date.now(),
    highestTierRank: meta.highestTierRankReached,
    causeOfDeath: 'voluntary retirement',
    finalNetWorth,
    runDurationMs: 0,
  });
  meta.awardCoins(earned);
  clearAllArcsForAscension();
  clearEnginePersist();

  // Reset per-run stores. Apply persistent buffs from meta.
  const buffs = useMeta.getState().buffs;
  useMarkets.setState({
    cash: 5_000 + buffs.startingCashBonus,
    positions: [],
    openOrders: [],
    realizedPnl: 0,
    recentFills: [],
    activeTickerId: 'TARI',
    equityATH: 0,
  });
  useLife.setState({
    relationships: {
      wife: 30 + buffs.startingWifeRepBonus,
      mom: 60 + buffs.startingMomRepBonus,
      broker: 0 + buffs.startingBrokerRepBonus,
      lawyer: 0,
      best_friend: 50,
    },
    health: 80,
    sanity: 60 + buffs.startingSanityBonus,
    irsHeat: Math.max(0, 0 - buffs.startingIrsHeatReduction),
    secHeat: 0,
    fbiHeat: 0,
    apartmentTier: 'studio',
    insuranceStatus: 'normal',
    creditScore: 680,
    lawyerRetainerActiveSince: null,
    lastDecayMs: Date.now(),
  });
  useWallet.setState({
    possessions: [
      { id: 'dog', label: 'The dog', description: '12 lbs of unconditional love. Sells well, ruins your life.', sellPrice: 300, available: true },
      { id: 'watch', label: 'Bonus Rolex', description: 'Heirloom. Pawnable. He will know.', sellPrice: 1500, available: true },
      { id: 'car', label: 'The car', description: '2019 sedan. Smells like vape. Liquid for the price.', sellPrice: 5000, available: true },
      { id: 'house', label: 'The house', description: '"Starter home." Mortgage included free of charge. Will move you to the office floor.', sellPrice: 50000, available: true },
    ],
    ownsCharityFoundation: false,
    ownsYacht: false,
    ownsJet: false,
    ownsIsland: false,
    ownsSportsTeam: false,
    ownsNewspaper: false,
  });
  usePhone.setState({
    contacts: usePhone.getState().contacts,
    messages: [],
    activeThreadId: null,
  });
  useXClone.setState({
    posts: [],
    followers: 14 + buffs.startingFollowersBonus,
  });
  useEmail.setState({
    emails: [],
    selectedId: null,
  });

  saveNow();

  // Ascension stinger fires alongside the cinematic
  import('@/audio/synth').then((m) => m.sounds.ascension());

  useCinematic.getState().fire({
    id: 'ascension',
    type: 'ascension',
    headline: `You retired. ${earned} Carry Coins earned.`,
    subhead: 'next character starts now. with whatever you keep.',
    durationMs: 5500,
  });
}
